/**
 * Скрины переписок во вкладке «Новый год» (29.10.2026): ужатие на телефоне перед отправкой и раскладка по запросам.
 * Основа — shrink() из screens/op/waiverAttach.tsx, но с другим правилом: длинный скрин-«простыня» (1170×8000) по длинной
 * стороне не ужимаем — текст стал бы нечитаемым (~290 px в ширину). Предел — ширина ≤ 1280 px и площадь ≤ 16 Мп; перекодируем
 * всегда (JPEG ~0.82): так из файла уходят EXIF и геометка снимка экрана. Не перекодировался — исходник НЕ отправляем.
 * Сервер (routes/ny/shots.ts): до 10 файлов за запрос, каждый ≤ 15 МБ, весь запрос ≤ 40 МБ.
 * У каждого снимка свой ключ (k_N): ответ потерялся (связь, таймаут, 5xx) — «Повторить» шлёт тот же ключ, и сервер
 * отдаёт уже лежащий скрин его id, а не кладёт ПДн клиента второй раз.
 */

export const SHOT_MAX_W = 1280;
export const SHOT_MAX_AREA = 16_000_000;
export const SHOT_QUALITY = 0.82;
export const SHOTS_PER_REQ = 10;
export const SHOT_MAX_BYTES = 15 * 1048576;
/** запас к 40 МБ сервера: границы multipart и поля w_N/h_N */
export const SHOTS_REQ_BYTES = 38 * 1048576;

/** Размер после ужатия: ширина ≤ 1280, площадь ≤ 16 Мп, пропорции сохраняются; меньше — как есть (не растягиваем). */
export function fitShot(w: number, h: number): { w: number; h: number } {
  if (!(w > 0 && h > 0)) return { w: 0, h: 0 };
  const k = Math.min(1, SHOT_MAX_W / w, Math.sqrt(SHOT_MAX_AREA / (w * h)));
  if (k >= 1) return { w: Math.round(w), h: Math.round(h) };
  /* округляем, а не отсекаем (8000 × 0.16 в плавающей точке — 1279.99…), но пределы не переступаем */
  const W = Math.max(1, Math.min(SHOT_MAX_W, Math.round(w * k)));
  let H = Math.max(1, Math.round(h * k));
  if (W * H > SHOT_MAX_AREA) H = Math.floor(SHOT_MAX_AREA / W);
  return { w: W, h: H };
}

/** Раскладка файлов по запросам: в каждом ≤ 10 штук и ≤ 38 МБ; порядок сохраняется. Индексы исходного списка. */
export function planBatches(sizes: number[], perReq = SHOTS_PER_REQ, maxBytes = SHOTS_REQ_BYTES): number[][] {
  const out: number[][] = [];
  let cur: number[] = [], sum = 0;
  sizes.forEach((size, i) => {
    if (cur.length && (cur.length >= perReq || sum + size > maxBytes)) { out.push(cur); cur = []; sum = 0; }
    cur.push(i); sum += size;
  });
  if (cur.length) out.push(cur);
  return out;
}

/** key — ключ снимка: живёт с ужатым файлом до отправки, повтор шлёт его же */
export interface ShrunkShot { file: File; w: number; h: number; key: string }

/** Ключ снимка (сервер ждёт 16–64 знаков [A-Za-z0-9-]). Старый WebView без randomUUID — 16 случайных байт. */
export function newShotKey(): string {
  try {
    const c: Partial<Crypto> | undefined = globalThis.crypto;
    if (c?.randomUUID) return c.randomUUID();
    if (c?.getRandomValues) return Array.from(c.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
  } catch { /* совсем без crypto — ниже; ключ короче 16 сервер не берёт: снимок ляжет, как без ключа */ }
  return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`.padEnd(16, "0");
}

/** Тело одного запроса: shot_N + w_N, h_N, k_N; N — номер в запросе, ключ — снимка (повтор другой пачкой — тот же ключ). */
export function shotsForm(ready: ShrunkShot[], batch: number[]): FormData {
  const fd = new FormData();
  batch.forEach((idx, n) => {
    const s = ready[idx]!;
    fd.append(`shot_${n}`, s.file); fd.append(`w_${n}`, String(s.w)); fd.append(`h_${n}`, String(s.h)); fd.append(`k_${n}`, s.key);
  });
  return fd;
}

/** Ужать и перекодировать снимок (canvas → JPEG). Ориентация — по EXIF, как показывает <img>; прозрачность — на белом. */
export async function shrinkShot(f: File, n: number): Promise<ShrunkShot> {
  /* тип пустой бывает у HEIC из галереи Android — пробуем открыть; не картинку не откроет createImageBitmap */
  if ((f.type && !f.type.startsWith("image/")) || f.type === "image/svg+xml") throw new Error("Это не фото и не скриншот");
  const bmp = await createImageBitmap(f, { imageOrientation: "from-image" })
    .catch(() => { throw new Error("Не удалось открыть снимок — формат не поддерживается"); });
  try {
    const { w, h } = fitShot(bmp.width, bmp.height);
    if (!w || !h) throw new Error("Пустой снимок");
    const cv = document.createElement("canvas");
    cv.width = w; cv.height = h;
    const ctx = cv.getContext("2d");
    if (!ctx) throw new Error("Не удалось обработать снимок");
    ctx.fillStyle = "#fff";   // JPEG без прозрачности: прозрачный PNG иначе станет чёрным
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bmp, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((res) => cv.toBlob(res, "image/jpeg", SHOT_QUALITY));
    cv.width = 0; cv.height = 0;   // отдать память холста сразу (iOS держит её до сборки мусора)
    if (!blob) throw new Error("Не удалось обработать снимок — слишком большой");
    if (blob.size > SHOT_MAX_BYTES) throw new Error("Снимок больше 15 МБ даже после ужатия");
    return { file: new File([blob], `shot-${n + 1}.jpg`, { type: "image/jpeg" }), w, h, key: newShotKey() };
  } finally {
    bmp.close();
  }
}
